var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { useState, } from 'react';
import { css } from '@emotion/react';
import { Tooltip } from '../../display/Tooltip';
import { createUniqueCustomProperties } from '../../../utils/createUniqueCustomProperty';
import { Animation, Colors, Typography } from '../../../theme';
import { isolateComponent, labelStyles } from '@zapier/style-encapsulation';
import { uniqueId } from '../../../utils/uniqueId';
const Vars = createUniqueCustomProperties('Input', [
    'borderRadius',
    'iconColor',
    'inputValueColor',
    'inputHeight',
    'inputPaddingLeft',
    'inputPaddingRight',
    'inputPaddingTop',
    'inputPaddingBottom',
    'prependPaddingLeft',
    'appendPaddingRight',
    'outlineColor',
    'placeholderColor',
    'prependAppendColor',
    'tooltipOpacity',
]);
const Styles = labelStyles('Input', {
    root: (props) => {
        var _a;
        return css `
    ${Vars.borderRadius}: ${(_a = props.borderRadius) !== null && _a !== void 0 ? _a : 3}px;
    ${Vars.iconColor}: ${Colors.neutral600};
    // Input is 44px height including border
    ${Vars.inputHeight}: 42px;
    ${Vars.inputPaddingLeft}: 15px;
    ${Vars.inputPaddingRight}: 15px;
    ${Vars.inputPaddingTop}: 9px;
    ${Vars.inputPaddingBottom}: 9px;
    ${Vars.placeholderColor}: ${Colors.neutral600};
    ${Vars.inputValueColor}: ${Colors.neutral800};
    ${Vars.prependPaddingLeft}: 15px;
    ${Vars.appendPaddingRight}: 15px;
    ${Vars.outlineColor}: ${Colors.neutral600};
    ${Vars.prependAppendColor}: ${Colors.neutral800};
    ${Vars.tooltipOpacity}: 0;

    border: 1px solid var(${Vars.outlineColor});
    // Box-shadow only for focus state
    box-shadow: none;
    // For Tooltip positioning
    position: relative;
    border-radius: var(${Vars.borderRadius});
    background-color: ${Colors.neutral100};

    // Only transition specific properties because some,
    // like 'padding', will change but shouldn't transition.
    transition-duration: ${Animation.transitionDuration};
    transition-property: background-color, color, box-shadow;
    transition-timing-function: ease;

    &[data-errored] {
      ${Vars.outlineColor}: ${Colors.error400};
    }

    &:not([data-disabled]):hover {
      ${Vars.outlineColor}: ${Colors.blue};
    }

    &:not([data-disabled]):focus-within {
      ${Vars.outlineColor}: ${Colors.blue};
      box-shadow: 0 0 0 1px var(${Vars.outlineColor});
    }

    &[data-disabled] {
      ${Vars.prependAppendColor}: ${Colors.neutral500};
      ${Vars.outlineColor}: ${Colors.neutral400};
      ${Vars.iconColor}: ${Colors.neutral400};
      ${Vars.inputValueColor}: ${Colors.neutral500};
      ${Vars.placeholderColor}: ${Colors.neutral500};
      cursor: not-allowed;
      background-color: ${Colors.neutral200};
    }

    &[data-readonly]:hover,
    &[data-disabled]:hover {
      ${Vars.tooltipOpacity}: 1;
    }

    &[data-full-width] {
      width: 100%;
      max-width: 100%;
    }

    &[data-icon-before] {
      ${Vars.prependPaddingLeft}: 0;
      ${Vars.inputPaddingLeft}: 0;
    }

    &[data-prepend][data-layout='inline'] {
      ${Vars.inputPaddingLeft}: 0;
    }

    &[data-icon-after] {
      ${Vars.appendPaddingRight}: 0;
      ${Vars.inputPaddingRight}: 0;
    }

    &[data-append][data-layout='inline'] {
      ${Vars.inputPaddingRight}: 0;
    }
  `;
    },
    inputLayout: css `
    display: flex;
    flex-wrap: wrap;
    align-items: center;
  `,
    prependAppend: css `
    color: var(${Vars.prependAppendColor});

    &[data-layout='inline'] {
      ${Typography.paragraph3Medium};

      &[data-type='prepend'] {
        margin-left: var(${Vars.prependPaddingLeft});
        margin-right: 10px;
      }

      &[data-type='append'] {
        margin-left: 10px;
        margin-right: var(${Vars.appendPaddingRight});
      }
    }

    &[data-layout='block'] {
      ${Typography.smallPrint1};
      background-color: ${Colors.neutral300};
      display: flex;
      align-items: center;
      width: 100%;
      height: 30px;
      padding: 0 var(${Vars.inputPaddingLeft});

      &[data-type='prepend'] {
        // Ensure prepend renders before icon.
        order: -1;
        border-top-left-radius: var(${Vars.borderRadius});
        border-top-right-radius: var(${Vars.borderRadius});
      }

      &[data-type='append'] {
        // Ensure append renders after icon.
        // The value is a contrived number that needs to be
        // greater than the number of siblings it has.
        order: 10;
        border-bottom-left-radius: var(${Vars.borderRadius});
        border-bottom-right-radius: var(${Vars.borderRadius});
      }
    }
  `,
    input: css `
    flex: 1;
    ${Typography.paragraph3Medium};
    color: var(${Vars.inputValueColor});
    height: 100%;
    padding-top: var(${Vars.inputPaddingTop});
    padding-bottom: var(${Vars.inputPaddingBottom});
    padding-left: var(${Vars.inputPaddingLeft});
    padding-right: var(${Vars.inputPaddingRight});

    &:not(textarea) {
      height: var(${Vars.inputHeight});
    }

    // Style placeholder. Selectors cannot be combined,
    // so keep 'em separated.
    &::placeholder {
      color: var(${Vars.placeholderColor});
      // Necessary for Safari.
      -webkit-text-fill-color: var(${Vars.placeholderColor});
    }
    &::-moz-placeholder {
      color: var(${Vars.placeholderColor});
      // Firefox has <1 'opacity' on its placeholders,
      // so it has to be overridden.
      opacity: 1;
    }

    // Bump font size for devices that don't support hovering
    // to prevent mobile browsers from zooming in on inputs
    // when they're focused. 16px is defined at the OS level
    // of these devices, specifically iOS.
    @media (hover: none) {
      font-size: 16px;
    }

    // Hide "clear selection" X icon in Edge.
    // https://zappy.zapier.com/F693DF53-79B7-4857-AADE-73E9BF3011DB.png
    &::-ms-clear {
      display: none;
    }
    // Hide "clear selection" X icon in Safari.
    &::-webkit-search-cancel-button {
      display: none;
    }

    // Hide selection when '[readonly]' since the text can't
    // be edited, but the selection makes it look like it can be.
    &[readonly]::selection {
      background-color: transparent;
    }

    &[disabled] {
      cursor: not-allowed;
      color: ${Colors.neutral500};
      -webkit-text-fill-color: ${Colors.neutral500};
      white-space: pre-wrap;
      ${Vars.placeholderColor}: ${Colors.neutral500};
    }
  `,
    icon: css `
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(${Vars.iconColor});
    position: relative;
    z-index: 1;
    pointer-events: none;
    margin: 0 8px;
    height: var(${Vars.inputHeight});

    &[data-interactive] {
      pointer-events: auto;
    }
  `,
    tooltip: css `
    opacity: var(${Vars.tooltipOpacity});
    transition: ${Animation.transitionValue};
    position: absolute;
    left: 50%;
    transform: translate(0, -15px);
    z-index: 2;
  `,
});
export const Input = (_a) => {
    var { canClickIconBefore = true, canClickIconAfter = true, disabledText = 'Disabled', readOnlyText = 'Read-Only', isFocusable = true, layout = 'inline', isFullWidth = false, size = 'small', borderRadius = 3 } = _a, otherProps = __rest(_a, ["canClickIconBefore", "canClickIconAfter", "disabledText", "readOnlyText", "isFocusable", "layout", "isFullWidth", "size", "borderRadius"]);
    const props = Object.assign({ canClickIconBefore,
        canClickIconAfter,
        disabledText,
        readOnlyText,
        isFocusable,
        layout,
        isFullWidth,
        size,
        borderRadius }, otherProps);
    const [isFocused, setIsFocused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [tooltipId] = useState(props.id ? `${props.id}-tooltip` : uniqueId(`Input__tooltip-`));
    const shouldRenderTooltip = (props.isDisabled && !!props.disabledText) ||
        (props.isReadonly && !!props.readOnlyText);
    const renderIconProps = {
        iconSize: 24,
        isFocused,
        isHovered,
    };
    const iconBefore = props.renderIconBefore && props.renderIconBefore(renderIconProps);
    const iconAfter = props.renderIconAfter && props.renderIconAfter(renderIconProps);
    const derivedState = {
        hasIconBefore: !!iconBefore,
        hasIconAfter: !!iconAfter,
        isFocused,
        isHovered,
    };
    return (isolateComponent("div", { css: Styles.root(props), "data-append": props.appendContent ? '' : undefined, "data-disabled": props.isDisabled ? '' : undefined, "data-readonly": props.isReadonly ? '' : undefined, "data-errored": props.isErrored ? '' : undefined, "data-full-width": props.isFullWidth ? '' : undefined, "data-icon-after": iconAfter ? '' : undefined, "data-icon-before": iconBefore ? '' : undefined, "data-layout": props.layout, "data-prepend": props.prependContent ? '' : undefined, "data-size": props.size, onBlur: () => setIsFocused(false), onClick: props.onClick, onFocus: () => setIsFocused(true), onMouseEnter: () => setIsHovered(true), onMouseLeave: () => setIsHovered(false), role: "presentation" },
        isolateComponent("div", { css: Styles.inputLayout },
            iconBefore && (isolateComponent("div", { css: Styles.icon, "data-interactive": props.canClickIconBefore ? '' : undefined, "data-size": props.size }, iconBefore)),
            props.prependContent && (isolateComponent("div", { css: Styles.prependAppend, "data-layout": props.layout, "data-size": props.size, "data-type": "prepend" }, props.prependContent)),
            props.children({
                'aria-activedescendant': props.ariaActiveDescendant,
                'aria-controls': props.ariaControls,
                'aria-describedby': shouldRenderTooltip
                    ? tooltipId
                    : props.ariaDescribedBy,
                'aria-errormessage': props.isErrored
                    ? props.ariaDescribedBy
                    : undefined,
                'aria-hidden': props.ariaHidden,
                'aria-invalid': props.isErrored,
                'aria-label': props.ariaLabel,
                'aria-labelledby': props.ariaLabelledBy,
                'aria-required': props.ariaRequired,
                'data-size': props.size,
                autoComplete: props.autoComplete,
                autoFocus: props.autoFocus,
                css: Styles.input,
                tabIndex: props.isFocusable ? undefined : -1,
                disabled: props.isDisabled,
                id: props.id,
                maxLength: props.maxLength,
                name: props.name,
                onBlur: props.onBlur,
                onChange: props.onChange,
                onFocus: props.onFocus,
                onKeyDown: props.onKeyDown,
                placeholder: props.placeholder,
                readOnly: props.isReadonly,
                required: props.isRequired,
                role: props.role,
                value: props.value,
            }, derivedState),
            props.appendContent && (isolateComponent("div", { css: Styles.prependAppend, "data-layout": props.layout, "data-type": "append", "data-size": props.size }, props.appendContent)),
            iconAfter && (isolateComponent("div", { css: Styles.icon, "data-interactive": props.canClickIconAfter ? '' : undefined, "data-size": props.size }, iconAfter))),
        shouldRenderTooltip && (isolateComponent("div", { css: Styles.tooltip },
            isolateComponent(Tooltip, { id: tooltipId, position: "south" }, props.isReadonly ? props.readOnlyText : props.disabledText)))));
};
//# sourceMappingURL=Input.js.map
// @zapier/design-system@6.14.1 (10126c41d)
